import axios, { CancelToken } from 'axios';

import { IOrgaoVinculado } from '../../../../pages/SaldoDeContas/Orgaos/interfaces';
import { IBancoVanBancaria, IContasEPlanilhaResponse } from '../../../../pages/SaldoDeContas/interfaces';
import { IOrdenacaoCategoriaItem } from '../../../../pages/SaldoDeContas/SolicitacoesDeAtivacao/Gerenciar/Tabs/AlinhamentoDeContasEFontes/interfaces';
import { IAutorizacao, IBancoSaldoDeContas, ICategoria, IContaComErro, IContaSaldoDeContas, IFonteSaldoDeContas, IOrgaoAutorizacaoItem, IOrgaoComErro, ISistemaContabil, ISolicitacaoAtivacao, ISolicitacaoAtivacaoListItem, IUsuarioSolicitacao } from '../../../../pages/SaldoDeContas/SolicitacoesDeAtivacao/interfaces';
import { IDescobertaCnpjContaResponse } from '../../../../pages/SaldoDeContas/SolicitacoesDeAtivacao/Gerenciar/Tabs/IntegracaoComBancos/interfaces';
import { IInvestimentoAtivacao } from '../../../../pages/SaldoDeContas/InvestimentosACadastrar/interfaces';

import { GATEWAY_API_URL } from "../../../request/urls";
import { getToken } from "../../../token";

class SolicitacoesDeAtivacaoService {

    async getSolicitacoesAtivacao(signal?: CancelToken): Promise<ISolicitacaoAtivacaoListItem[]> {

        const { data } = await axios.get(`${GATEWAY_API_URL}/saldo-empenhos/solicitacao-ativacao`, {
            headers: {
                'Authorization': 'Bearer ' + getToken()
            },
            cancelToken: signal
        });

        return data.solicitacoes;
    }

    async getSolicitacaoAtivacaoById({
        id,
        signal
    }: {
        id: number,
        signal?: AbortSignal
    }): Promise<ISolicitacaoAtivacao> {

        const { data } = await axios.get(`${GATEWAY_API_URL}/saldo-empenhos/solicitacao-ativacao/${id}`, {
            headers: {
                'Authorization': 'Bearer ' + getToken()
            },
            signal
        });

        return data;
    }

    async getSistemasContabeis(signal?: AbortSignal): Promise<ISistemaContabil[]> {

        const { data } = await axios.get(`${GATEWAY_API_URL}/saldo-empenhos/sistema-contabil`, {
            headers: {
                'Authorization': 'Bearer ' + getToken()
            },
            signal
        });

        return data.sistemasContabeis;
    }

    async updateSistemaContabil({
        solicitacaoId,
        sistemaContabilId,
        signal,
    }: {
        solicitacaoId: number,
        sistemaContabilId: number,
        signal?: AbortSignal
    }): Promise<ISolicitacaoAtivacao> {
        const { data } = await axios.patch(
            `${GATEWAY_API_URL}/saldo-empenhos/solicitacao-ativacao/${solicitacaoId}/sistema-contabil`,
            { sistemaContabilId },
            {
                headers: {
                    'Authorization': 'Bearer ' + getToken()
                },
                signal
            }
        );

        return data;
    }

    async uploadPlanilhaDeContas({
        solicitacaoId,
        formData,
    }: {
        solicitacaoId: number,
        formData: FormData,
    }): Promise<ISolicitacaoAtivacao> {
        const { data } = await axios.post(
            `${GATEWAY_API_URL}/saldo-empenhos/solicitacao-ativacao/${solicitacaoId}/upload-planilha`,
            formData,
            {

                headers: {
                    'Content-Type': 'multipart/form-data',
                    'Authorization': 'Bearer ' + getToken(),
                },
            }
        )

        return data;
    }

    async getContasEPlanilha({
        solicitacaoId,
        signal
    }: {
        solicitacaoId: number,
        signal?: AbortSignal
    }): Promise<IContasEPlanilhaResponse> {

        const { data } = await axios.get(`${GATEWAY_API_URL}/saldo-empenhos/solicitacao-ativacao/${solicitacaoId}/contas`, {
            headers: {
                'Authorization': 'Bearer ' + getToken()
            },
            signal
        });

        return data;
    }

    async iniciarEtapa({
        solicitacaoId,
        etapaId,
    }: {
        solicitacaoId: number,
        etapaId: number,
    }): Promise<ISolicitacaoAtivacao> {
        const { data } = await axios.post(
            `${GATEWAY_API_URL}/saldo-empenhos/solicitacao-ativacao/${solicitacaoId}/etapa/${etapaId}/iniciar`,
            undefined,
            {
                headers: {
                    'Authorization': 'Bearer ' + getToken()
                },
            }
        )

        return data;
    }

    async concluirEtapa({
        solicitacaoId,
        etapaId,
    }: {
        solicitacaoId: number,
        etapaId: number,
    }): Promise<ISolicitacaoAtivacao> {
        const { data } = await axios.post(
            `${GATEWAY_API_URL}/saldo-empenhos/solicitacao-ativacao/${solicitacaoId}/etapa/${etapaId}/concluir`,
            undefined,
            {
                headers: {
                    'Authorization': 'Bearer ' + getToken()
                },
            }
        )

        return data;
    }

    async updateObservacaoEtapa({
        solicitacaoId,
        etapaId,
        observacao,
    }: {
        solicitacaoId: number,
        etapaId: number,
        observacao: string,
    }): Promise<ISolicitacaoAtivacao> {
        const { data } = await axios.patch(
            `${GATEWAY_API_URL}/saldo-empenhos/solicitacao-ativacao/${solicitacaoId}/etapa/${etapaId}/observacao`,
            { observacao },
            {
                headers: {
                    'Authorization': 'Bearer ' + getToken()
                },
            }
        )

        return data;
    }

    async getAutorizacoesBancoDoBrasil({
        solicitacaoId,
        signal
    }: {
        solicitacaoId: number,
        signal?: AbortSignal
    }): Promise<IOrgaoAutorizacaoItem[]> {

        const { data } = await axios.get(`${GATEWAY_API_URL}/saldo-empenhos/solicitacao-ativacao/${solicitacaoId}/autorizacao-banco-do-brasil`, {
            headers: {
                'Authorization': 'Bearer ' + getToken()
            },
            signal
        });

        return data.orgaosVinculadosAAutorizar.map((e: any) => ({
            ...e,
            concluidaEm: e.autorizacaoBB ? e.autorizacaoBB.concluidaEm : null,
        }));
    }

    async createIdAplicacao({
        solicitacaoId,
        orgaoVinculadoId,
        bbAplicacaoId,
        versaoApi,
    }: {
        solicitacaoId: number,
        orgaoVinculadoId: number,
        bbAplicacaoId: string,
        versaoApi: number,
    }): Promise<IAutorizacao> {
        const { data } = await axios.post(
            `${GATEWAY_API_URL}/saldo-empenhos/solicitacao-ativacao/${solicitacaoId}/autorizacao-banco-do-brasil`,
            { orgaoVinculadoId, bbAplicacaoId, versaoApi },
            {
                headers: {
                    'Authorization': 'Bearer ' + getToken()
                },
            }
        )

        return data;
    }

    async updateIdAplicacao({
        solicitacaoId,
        autorizacaoBbId,
        bbAplicacaoId,
        versaoApi,
    }: {
        solicitacaoId: number,
        autorizacaoBbId: number,
        bbAplicacaoId: string,
        versaoApi: number,
    }): Promise<IAutorizacao> {
        const { data } = await axios.patch(
            `${GATEWAY_API_URL}/saldo-empenhos/solicitacao-ativacao/${solicitacaoId}/autorizacao-banco-do-brasil/${autorizacaoBbId}`,
            { bbAplicacaoId, versaoApi },
            {
                headers: {
                    'Authorization': 'Bearer ' + getToken()
                },
            }
        )

        return data;
    }

    async deleteAutorizacao({
        solicitacaoId,
        autorizacaoId,
    }: {
        solicitacaoId: number,
        autorizacaoId: number,
    }): Promise<IAutorizacao> {
        const { data } = await axios.delete(
            `${GATEWAY_API_URL}/saldo-empenhos/solicitacao-ativacao/${solicitacaoId}/autorizacao-banco/${autorizacaoId}`,
            {
                headers: {
                    'Authorization': 'Bearer ' + getToken()
                },
            }
        )

        return data;
    }

    async createCredenciais({
        solicitacaoId,
        autorizacaoBbId,
        basic,
        appKey,
        clientId,
        clientSecret,
    }: {
        solicitacaoId: number,
        autorizacaoBbId: number,
        basic: string,
        appKey: string,
        clientId: string,
        clientSecret: string,
    }): Promise<IAutorizacao> {
        const { data } = await axios.post(
            `${GATEWAY_API_URL}/saldo-empenhos/solicitacao-ativacao/${solicitacaoId}/autorizacao-banco-do-brasil/${autorizacaoBbId}/credencial`,
            { basic, appKey, clientId, clientSecret },
            {
                headers: {
                    'Authorization': 'Bearer ' + getToken()
                },
            }
        )

        return data;
    }

    async updateCredenciais({
        solicitacaoId,
        autorizacaoBbId,
        basic,
        appKey,
        clientId,
        clientSecret,
    }: {
        solicitacaoId: number,
        autorizacaoBbId: number,
        basic: string,
        appKey: string,
        clientId: string,
        clientSecret: string,
    }): Promise<IAutorizacao> {
        const { data } = await axios.put(
            `${GATEWAY_API_URL}/saldo-empenhos/solicitacao-ativacao/${solicitacaoId}/autorizacao-banco-do-brasil/${autorizacaoBbId}/credencial`,
            { basic, appKey, clientId, clientSecret },
            {
                headers: {
                    'Authorization': 'Bearer ' + getToken()
                },
            }
        )

        return data;
    }

    async getContasComErro({
        solicitacaoId,
        signal
    }: {
        solicitacaoId: number,
        signal?: AbortSignal
    }): Promise<IOrgaoComErro[]> {

        const { data } = await axios.get(`${GATEWAY_API_URL}/saldo-empenhos/solicitacao-ativacao/${solicitacaoId}/contas-com-erro`, {
            headers: {
                'Authorization': 'Bearer ' + getToken()
            },
            signal
        });

        return data.orgaosVinculados;
    }

    async getContasInativas({
        solicitacaoId,
        signal
    }: {
        solicitacaoId: number,
        signal?: AbortSignal
    }): Promise<IOrgaoComErro[]> {

        const { data } = await axios.get(`${GATEWAY_API_URL}/saldo-empenhos/solicitacao-ativacao/${solicitacaoId}/contas-inativas`, {
            headers: {
                'Authorization': 'Bearer ' + getToken()
            },
            signal
        });

        return data.orgaosVinculados;
    }

    async updateContaAtiva({
        solicitacaoId,
        contaId,
        ativa,
    }: {
        solicitacaoId: number,
        contaId: number,
        ativa: boolean,
    }): Promise<IContaComErro> {

        const { data } = await axios.patch(
            `${GATEWAY_API_URL}/saldo-empenhos/solicitacao-ativacao/${solicitacaoId}/contas-com-erro/${contaId}`,
            {
                ativa,
            },
            {
                headers: {
                    'Authorization': 'Bearer ' + getToken()
                },
            }
        );

        return data;
    }

    async getUsuariosSolicitacao({
        solicitacaoId,
        signal
    }: {
        solicitacaoId: number,
        signal?: AbortSignal
    }): Promise<IUsuarioSolicitacao[]> {

        const { data } = await axios.get(`${GATEWAY_API_URL}/saldo-empenhos/solicitacao-ativacao/${solicitacaoId}/usuarios`, {
            headers: {
                'Authorization': 'Bearer ' + getToken()
            },
            signal
        });

        return data.usuarios;
    }

    async getCategorias({
        signal
    }: {
        signal?: AbortSignal
    }): Promise<ICategoria[]> {

        const { data } = await axios.get(`${GATEWAY_API_URL}/saldo-empenhos/fonte/categoria`, {
            headers: {
                'Authorization': 'Bearer ' + getToken()
            },
            signal
        });

        return data.categorias;
    }

    async updateCategoriasUsuarioSolicitacao({
        solicitacaoId,
        usuarioId,
        categoriaIds,
        signal
    }: {
        solicitacaoId: number,
        usuarioId: number,
        categoriaIds: number[],
        signal?: AbortSignal
    }): Promise<IUsuarioSolicitacao> {
        const { data } = await axios.put(
            `${GATEWAY_API_URL}/saldo-empenhos/solicitacao-ativacao/${solicitacaoId}/usuario/${usuarioId}/categorias`,
            { categoriaIds },
            {
                headers: {
                    'Authorization': 'Bearer ' + getToken()
                },
                signal
            }
        );

        return {
            ...data.usuario,
            categorias: data.categorias,
        };
    }

    async getBancos({
        signal
    }: {
        signal?: AbortSignal
    }): Promise<IBancoSaldoDeContas[]> {

        const { data } = await axios.get(`${GATEWAY_API_URL}/saldo-empenhos/bancos`, {
            headers: {
                'Authorization': 'Bearer ' + getToken()
            },
            signal
        });

        return data.bancos;
    }

    async getFontes({
        solicitacaoId,
        signal
    }: {
        solicitacaoId: number,
        signal?: AbortSignal
    }): Promise<IFonteSaldoDeContas[]> {

        const { data } = await axios.get(`${GATEWAY_API_URL}/saldo-empenhos/solicitacao-ativacao/${solicitacaoId}/fontes`, {
            headers: {
                'Authorization': 'Bearer ' + getToken()
            },
            signal
        });

        return data.fontes;
    }

    async createFonte({
        solicitacaoId,
        numero,
        descricao,
        categoriaId,
    }: {
        solicitacaoId: number,
        numero: string,
        descricao: string,
        categoriaId: number,
    }): Promise<IFonteSaldoDeContas> {

        const { data } = await axios.post(
            `${GATEWAY_API_URL}/saldo-empenhos/solicitacao-ativacao/${solicitacaoId}/fontes`,
            {
                numero,
                descricao,
                categoriaId,
            },
            {
                headers: {
                    'Authorization': 'Bearer ' + getToken()
                },
            }
        );

        return data;
    }

    async updateDescricaoFonte({
        solicitacaoId,
        numero,
        descricao,
    }: {
        solicitacaoId: number,
        numero: string,
        descricao: string,
    }): Promise<IFonteSaldoDeContas | null> {

        const { data } = await axios.put(
            `${GATEWAY_API_URL}/saldo-empenhos/solicitacao-ativacao/${solicitacaoId}/fontes`,
            {
                numero,
                descricao,
            },
            {
                headers: {
                    'Authorization': 'Bearer ' + getToken()
                },
            }
        );

        const fontes = data.fontes as IFonteSaldoDeContas[];

        return fontes.find((e) => e.numero === numero) || null;
    }

    async updateDescricaoFonteConsolidada({
        solicitacaoId,
        numero,
        descricao,
    }: {
        solicitacaoId: number,
        numero: string,
        descricao: string,
    }): Promise<IFonteSaldoDeContas | null> {

        const { data } = await axios.put(
            `${GATEWAY_API_URL}/saldo-empenhos/solicitacao-ativacao/${solicitacaoId}/nome-fonte-consolidada`,
            {
                numero: numero.split(','),
                nome: descricao,
            },
            {
                headers: {
                    'Authorization': 'Bearer ' + getToken()
                },
            }
        );

        return data;
    }

    async updateCategoriaFonte({
        solicitacaoId,
        numero,
        categoriaId,
    }: {
        solicitacaoId: number,
        numero: string,
        categoriaId: number,
    }): Promise<void> {

        const { data } = await axios.put(
            `${GATEWAY_API_URL}/saldo-empenhos/solicitacao-ativacao/${solicitacaoId}/categoria-fonte-consolidada`,
            {
                numero: numero.split(','),
                categoriaId,
            },
            {
                headers: {
                    'Authorization': 'Bearer ' + getToken()
                },
            }
        );

        return data;
    }

    async createConta({
        solicitacaoId,
        orgaoVinculadoId,
        descricao,
        numero,
        agencia,
        bancoId,
        fonteIds,
    }: {
        solicitacaoId: number,
        orgaoVinculadoId: number,
        descricao: string,
        numero: string,
        agencia: string,
        bancoId: number,
        fonteIds: number[],
    }): Promise<IContaSaldoDeContas> {

        const { data } = await axios.post(
            `${GATEWAY_API_URL}/saldo-empenhos/solicitacao-ativacao/${solicitacaoId}/contas`,
            {
                orgaoVinculadoId,
                descricao,
                numero,
                agencia,
                bancoId,
                fonteIds,
            },
            {
                headers: {
                    'Authorization': 'Bearer ' + getToken()
                },
            }
        );

        return data;
    }

    async updateConta({
        solicitacaoId,
        contaId,
        orgaoVinculadoId,
        descricao,
        numero,
        agencia,
        bancoId,
        fonteIds,
    }: {
        solicitacaoId: number,
        contaId: number,
        orgaoVinculadoId: number,
        descricao: string,
        numero: string,
        agencia: string,
        bancoId: number,
        fonteIds: number[],
    }): Promise<IContaSaldoDeContas> {

        const { data } = await axios.put(
            `${GATEWAY_API_URL}/saldo-empenhos/solicitacao-ativacao/${solicitacaoId}/contas/${contaId}`,
            {
                orgaoVinculadoId,
                descricao,
                numero,
                agencia,
                bancoId,
                fonteIds,
            },
            {
                headers: {
                    'Authorization': 'Bearer ' + getToken()
                },
            }
        );

        return data;
    }

    async deleteConta({
        solicitacaoId,
        contaId,
    }: {
        solicitacaoId: number,
        contaId: number,
    }): Promise<IContaSaldoDeContas> {
        const { data } = await axios.delete(`${GATEWAY_API_URL}/saldo-empenhos/solicitacao-ativacao/${solicitacaoId}/contas/${contaId}`, {
            headers: {
                'Authorization': 'Bearer ' + getToken()
            },
        });

        return data;
    }

    async deleteFonte({
        solicitacaoId,
        fonteId,
    }: {
        solicitacaoId: number,
        fonteId: number,
    }): Promise<void> {
        await axios.delete(`${GATEWAY_API_URL}/saldo-empenhos/solicitacao-ativacao/${solicitacaoId}/fonte/${fonteId}`, {
            headers: {
                'Authorization': 'Bearer ' + getToken()
            },
        });
    }

    async updateContasFontes({
        solicitacaoId,
        contaIds,
        fonteIds,
    }: {
        solicitacaoId: number,
        contaIds: number[],
        fonteIds: number[],
    }): Promise<IContaSaldoDeContas[]> {
        const { data } = await axios.put(
            `${GATEWAY_API_URL}/saldo-empenhos/solicitacao-ativacao/${solicitacaoId}/contas/fontes`,
            {
                contaIds,
                fonteIds,
            },
            {
                headers: {
                    'Authorization': 'Bearer ' + getToken()
                },
            }
        );

        return data;
    }

    async updateSistemaContabilIntegrado({
        solicitacaoId,
        integrado,
    }: {
        solicitacaoId: number,
        integrado: boolean
    }): Promise<ISolicitacaoAtivacao> {
        const { data } = await axios.patch(
            `${GATEWAY_API_URL}/saldo-empenhos/solicitacao-ativacao/${solicitacaoId}/empenhos-e-restos`,
            { integrado },
            {
                headers: {
                    'Authorization': 'Bearer ' + getToken()
                },
            }
        );

        return data;
    }

    async updateTipoEmpenhosERestos({
        solicitacaoId,
        tipo,
    }: {
        solicitacaoId: number,
        tipo: string,
    }): Promise<ISolicitacaoAtivacao> {
        const { data } = await axios.patch(
            `${GATEWAY_API_URL}/saldo-empenhos/solicitacao-ativacao/${solicitacaoId}/tipo-empenhos-e-restos`,
            { tipo },
            {
                headers: {
                    'Authorization': 'Bearer ' + getToken()
                },
            }
        );

        return data;
    }

    async getQuantidadeSocilitacoesNaoConcluidas(): Promise<number> {

        const { data } = await axios.get(`${GATEWAY_API_URL}/saldo-empenhos/solicitacao-ativacao/quantidade-nao-concluidas`, {
            headers: {
                'Authorization': 'Bearer ' + getToken()
            },
        });

        return data.quantidade_nao_concluidas;
    }

    async createOrgao({
        solicitacaoId,
        orgaosVinculados,
    }: {
        solicitacaoId: number,
        orgaosVinculados: {
            nome: string,
            cnpj: string,
        }[],
    }): Promise<ISolicitacaoAtivacao> {

        const { data } = await axios.post(
            `${GATEWAY_API_URL}/saldo-empenhos/solicitacao-ativacao/${solicitacaoId}/orgao`,
            {
                orgaosVinculados,
            },
            {
                headers: {
                    'Authorization': 'Bearer ' + getToken()
                },
            }
        );

        return data;
    }

    async createOrgaoVinculado({
        solicitacaoId,
        orgaoVinculado,
    }: {
        solicitacaoId: number,
        orgaoVinculado: {
            nome: string,
            cnpj: string,
        },
    }): Promise<ISolicitacaoAtivacao> {

        const { data } = await axios.post(
            `${GATEWAY_API_URL}/saldo-empenhos/solicitacao-ativacao/${solicitacaoId}/orgao-vinculado`,
            orgaoVinculado,
            {
                headers: {
                    'Authorization': 'Bearer ' + getToken()
                },
            }
        );

        return data;
    }

    async updateOrgaoVinculado({
        solicitacaoId,
        orgaoVinculadoId,
        orgaoVinculado,
    }: {
        solicitacaoId: number,
        orgaoVinculadoId: number,
        orgaoVinculado: {
            nome: string,
            cnpj: string,
        },
    }): Promise<ISolicitacaoAtivacao> {

        const { data } = await axios.put(
            `${GATEWAY_API_URL}/saldo-empenhos/solicitacao-ativacao/${solicitacaoId}/orgao-vinculado/${orgaoVinculadoId}`,
            orgaoVinculado,
            {
                headers: {
                    'Authorization': 'Bearer ' + getToken()
                },
            }
        );

        return data;
    }

    async createConfiguracaoSistemaContabil({
        solicitacaoId,
        configuracao,
    }: {
        solicitacaoId: number,
        configuracao: Object,
    }): Promise<ISolicitacaoAtivacao> {

        const { data } = await axios.post(
            `${GATEWAY_API_URL}/saldo-empenhos/solicitacao-ativacao/${solicitacaoId}/configuracao-sistema-contabil`,
            configuracao,
            {
                headers: {
                    'Authorization': 'Bearer ' + getToken()
                },
            }
        );

        return data;
    }

    async updateConfiguracaoSistemaContabil({
        solicitacaoId,
        configuracao,
    }: {
        solicitacaoId: number,
        configuracao: Object,
    }): Promise<ISolicitacaoAtivacao> {

        const { data } = await axios.put(
            `${GATEWAY_API_URL}/saldo-empenhos/solicitacao-ativacao/${solicitacaoId}/configuracao-sistema-contabil`,
            configuracao,
            {
                headers: {
                    'Authorization': 'Bearer ' + getToken()
                },
            }
        );

        return data;
    }

    async deleteConfiguracaoSistemaContabil(solicitacaoId: number): Promise<ISolicitacaoAtivacao> {

        const { data } = await axios.delete(
            `${GATEWAY_API_URL}/saldo-empenhos/solicitacao-ativacao/${solicitacaoId}/configuracao-sistema-contabil`,
            {
                headers: {
                    'Authorization': 'Bearer ' + getToken()
                },
            }
        );

        return data;
    }

    async getOrdenacaoFontes({
        solicitacaoId
    }: {
        solicitacaoId: number,
    }): Promise<IOrdenacaoCategoriaItem[]> {

        const { data } = await axios.get(`${GATEWAY_API_URL}/saldo-empenhos/solicitacao-ativacao/${solicitacaoId}/fonte-consolidada`, {
            headers: {
                'Authorization': 'Bearer ' + getToken()
            },
        });

        return data.categorias;
    }

    async getFontesOrfas({
        solicitacaoId
    }: {
        solicitacaoId: number,
    }): Promise<IOrdenacaoCategoriaItem[]> {

        const { data } = await axios.get(`${GATEWAY_API_URL}/saldo-empenhos/solicitacao-ativacao/${solicitacaoId}/fonte-orfa`, {
            headers: {
                'Authorization': 'Bearer ' + getToken()
            },
        });

        return data.categorias;
    }

    async atualizarFonteFavorita({
        solicitacaoId,
        numeroFonte,
        favorita,
    }: {
        solicitacaoId: number,
        numeroFonte: string,
        favorita: boolean,
    }): Promise<unknown> {

        const { data } = await axios.put(`${GATEWAY_API_URL}/saldo-empenhos/solicitacao-ativacao/${solicitacaoId}/fonte-consolidada`, {
            numero: numeroFonte.split(','),
            favorita,
        }, {
            headers: {
                'Authorization': 'Bearer ' + getToken()
            },
        });

        return data;
    }

    async atualizarOrdenacaoFontes({
        solicitacaoId,
        numerosFontes,
        categoriaId,
    }: {
        solicitacaoId: number,
        numerosFontes: string[],
        categoriaId: number,
    }): Promise<IOrdenacaoCategoriaItem> {

        const { data } = await axios.post(`${GATEWAY_API_URL}/saldo-empenhos/solicitacao-ativacao/${solicitacaoId}/fonte-consolidada/categoria/${categoriaId}`, {
            fontes: numerosFontes.map((fonte) => ({
                numero: fonte.split(','),
            })),
        }, {
            headers: {
                'Authorization': 'Bearer ' + getToken()
            },
        });

        return data;
    }

    async deletePlanilha({
        solicitacaoId,
    }: {
        solicitacaoId: number,
    }): Promise<ISolicitacaoAtivacao> {
        const { data } = await axios.delete(`${GATEWAY_API_URL}/saldo-empenhos/solicitacao-ativacao/${solicitacaoId}/planilha`, {
            headers: {
                'Authorization': 'Bearer ' + getToken()
            },
        });

        return data;
    }

    async deleteOrgaoVinculado({
        solicitacaoId,
        orgaoVinculadoId,
    }: {
        solicitacaoId: number,
        orgaoVinculadoId: number,
    }): Promise<ISolicitacaoAtivacao> {
        const { data } = await axios.delete(`${GATEWAY_API_URL}/saldo-empenhos/solicitacao-ativacao/${solicitacaoId}/orgao-vinculado/${orgaoVinculadoId}`, {
            headers: {
                'Authorization': 'Bearer ' + getToken()
            },
        });

        return data;
    }

    async deleteOrgaosVinculados({
        solicitacaoId,
    }: {
        solicitacaoId: number,
    }): Promise<ISolicitacaoAtivacao> {
        const { data } = await axios.delete(`${GATEWAY_API_URL}/saldo-empenhos/solicitacao-ativacao/${solicitacaoId}/orgao-vinculado`, {
            headers: {
                'Authorization': 'Bearer ' + getToken()
            },
        });

        return data;
    }

    async getOrgaosVinculadosBySolicitacaoId({
        solicitacaoId,
        signal,
    }: {
        solicitacaoId: number,
        signal?: CancelToken,
    }): Promise<IOrgaoVinculado[] | null> {

        const { data } = await axios.get(`${GATEWAY_API_URL}/saldo-empenhos/solicitacao-ativacao/${solicitacaoId}/orgaos-vinculados`, {
            headers: {
                'Authorization': 'Bearer ' + getToken()
            },
            cancelToken: signal
        });

        return data.orgaosVinculados || null;
    }

    async descobreCnpjContaComErro({
        solicitacaoId,
        signal,
    }: {
        solicitacaoId: number,
        signal?: AbortSignal,
    }): Promise<IDescobertaCnpjContaResponse> {

        const { data } = await axios.get(`${GATEWAY_API_URL}/saldo-empenhos/solicitacao-ativacao/${solicitacaoId}/descobre-cnpj-conta-com-erro`, {
            headers: {
                'Authorization': 'Bearer ' + getToken()
            },
            signal
        });

        return data;
    }

    async getContaById({
        solicitacaoId,
        contaId,
        signal,
    }: {
        solicitacaoId: number,
        contaId: number,
        signal?: AbortSignal,
    }): Promise<IContaSaldoDeContas> {

        const { data } = await axios.get(`${GATEWAY_API_URL}/saldo-empenhos/solicitacao-ativacao/${solicitacaoId}/contas/${contaId}`, {
            headers: {
                'Authorization': 'Bearer ' + getToken()
            },
            signal
        });

        return data;
    }

    async getBancosVanBancaria({
        solicitacaoId,
        signal,
    }: {
        solicitacaoId: number,
        signal?: AbortSignal,
    }): Promise<IBancoVanBancaria[]> {

        const { data } = await axios.get(`${GATEWAY_API_URL}/saldo-empenhos/solicitacao-ativacao/${solicitacaoId}/autorizacao-van`, {
            headers: {
                'Authorization': 'Bearer ' + getToken()
            },
            signal
        });

        return data.bancosVanAAutorizar;
    }

    async updateNumeroRequerimento({
        solicitacaoId,
        autorizacaoId,
        numeroRequerimento,
        signal,
    }: {
        solicitacaoId: number,
        autorizacaoId: number,
        numeroRequerimento: string,
        signal?: AbortSignal,
    }): Promise<IBancoVanBancaria> {

        const { data } = await axios.patch(`${GATEWAY_API_URL}/saldo-empenhos/solicitacao-ativacao/${solicitacaoId}/autorizacao-van/${autorizacaoId}`, {
            numeroRequerimento
        }, {
            headers: {
                'Authorization': 'Bearer ' + getToken()
            },
            signal
        });

        return data;
    }

    async createCredenciaisVanBancaria({
        solicitacaoId,
        autorizacaoId,
        clientId,
        clientSecret,
        signal,
    }: {
        solicitacaoId: number,
        autorizacaoId: number,
        clientId: string,
        clientSecret: string,
        signal?: AbortSignal,
    }): Promise<IBancoVanBancaria> {

        const { data } = await axios.post(`${GATEWAY_API_URL}/saldo-empenhos/solicitacao-ativacao/${solicitacaoId}/autorizacao-van/${autorizacaoId}/credencial`, {
            clientId,
            clientSecret,
        }, {
            headers: {
                'Authorization': 'Bearer ' + getToken()
            },
            signal
        });

        return data;
    }

    async updateCredenciaisVanBancaria({
        solicitacaoId,
        autorizacaoId,
        clientId,
        clientSecret,
        signal,
    }: {
        solicitacaoId: number,
        autorizacaoId: number,
        clientId: string,
        clientSecret: string,
        signal?: AbortSignal,
    }): Promise<IBancoVanBancaria> {

        const { data } = await axios.put(`${GATEWAY_API_URL}/saldo-empenhos/solicitacao-ativacao/${solicitacaoId}/autorizacao-van/${autorizacaoId}/credencial`, {
            clientId,
            clientSecret,
        }, {
            headers: {
                'Authorization': 'Bearer ' + getToken()
            },
            signal
        });

        return data;
    }

    async getDocumentoVanBancariaSignedUrl({
        solicitacaoId,
        autorizacaoId,
        documentoId,
        signal,
    }: {
        solicitacaoId: number,
        autorizacaoId: number,
        documentoId: number,
        signal?: AbortSignal,
    }): Promise<{ url?: string; link?: string } | any> {

        const { data } = await axios.get(`${GATEWAY_API_URL}/saldo-empenhos/solicitacao-ativacao/${solicitacaoId}/autorizacao-van/${autorizacaoId}/documento/${documentoId}/link`, {
            headers: {
                'Authorization': 'Bearer ' + getToken()
            },
            signal
        });

        return data;
    }

    async getInvestimentos({
        solicitacaoId,
        signal,
    }: {
        solicitacaoId: number,
        signal?: CancelToken,
    }): Promise<IInvestimentoAtivacao[]> {
        const { data } = await axios.get(`${GATEWAY_API_URL}/saldo-empenhos/solicitacao-ativacao/${solicitacaoId}/investimento`, {
            headers: {
                'Authorization': 'Bearer ' + getToken()
            },
            cancelToken: signal
        });

        return data.investimentos;
    }

    async encerrarConta(contaId: number): Promise<void> {
        await axios.patch(`${GATEWAY_API_URL}/saldo-empenhos/conta/${contaId}/encerrar`, undefined, {
            headers: {
                'Authorization': 'Bearer ' + getToken()
            },
        });
    }
}

// eslint-disable-next-line import/no-anonymous-default-export
export default new SolicitacoesDeAtivacaoService();
